/**
 * Abteilungen (`/departments`): Organisationseinheiten, jede mit einem eigenen Lizenzschlüssel
 * aktiviert. Der Bereich des Kontrollzentrums dafür ist `controlCenter.departments`.
 *
 * `errors` ist nach den Fehlercodes des Servers benannt und wird dynamisch nachgeschlagen
 * (`utils/department/errors.ts`), ebenso `license.failures`; diese Schlüsselnamen sind also ein
 * Vertrag mit der API und nicht frei wählbar.
 */

export default {
	departments: {
		title: "Abteilungen",
		default: "Standard",
		list: {
			create: "Abteilung erstellen",
			empty: {
				title: "Sie gehören noch zu keiner Abteilung",
				hint: "Eine Abteilung ist eine Organisationseinheit mit eigenem Lizenzschlüssel. Bitten Sie einen Eigentümer der Abteilung, Sie hinzuzufügen.",
				createHint:
					"Eine Abteilung ist eine Organisationseinheit mit eigenem Lizenzschlüssel. Erstellen Sie eine mit dem Schlüssel, den LiveReader dafür ausgestellt hat, oder bitten Sie einen Eigentümer, Sie zu seiner Abteilung hinzuzufügen.",
			},
			column: {
				department: "Abteilung",
				role: "Ihre Rolle",
				license: "Lizenz",
			},
			everyone: "Alle sind Mitglied",
		},
		create: {
			title: "Abteilung erstellen",
			note: "Der Lizenzschlüssel wird zuerst beim Lizenzserver geprüft. Gesendet wird nur sein Hash, gespeichert wird der Schlüssel verschlüsselt.",
			name: "Name",
			description: "Beschreibung",
			licenseKey: "Lizenzschlüssel",
			licenseKeyHelp:
				"Der Schlüssel, den LiveReader für diese Abteilung ausgestellt hat. Es wird nichts angelegt, wenn der Lizenzserver ihn ablehnt.",
			submit: "Schlüssel prüfen und erstellen",
		},
		page: {
			sections: {
				overview: "Übersicht",
				spaces: "Spaces",
				members: "Mitglieder",
				seats: "Plätze",
				roles: "Rollen",
			},
			gone: {
				deleted: "Diese Abteilung wurde gelöscht",
				deletedNote: "Die Mitglieder und Rollen der Abteilung wurden ebenfalls gelöscht.",
				unavailable: "Diese Abteilung ist für Sie nicht verfügbar",
				unavailableNote: "Die Abteilung wurde gelöscht, oder Sie sind nicht mehr Mitglied.",
				back: "Zurück zu den Abteilungen",
			},
		},
		general: {
			title: "Angaben",
			name: "Name",
			description: "Beschreibung",
			members: "Mitglieder",
			projects: "Projekte",
			dataspaces: "Dataspaces",
			created: "Erstellt",
			yourRole: "Ihre Rolle",
			saved: "Abteilung gespeichert.",
			changedElsewhere:
				"Jemand anderes hat diese Abteilung geändert, während Sie sie bearbeitet haben. Ihre Änderung bleibt erhalten; verwerfen Sie sie, um die andere zu sehen.",
		},
		spaces: {
			title: "Spaces",
			empty: "In dieser Abteilung liegt noch kein Projekt und kein Dataspace.",
			noAccess: "Kein Zugriff",
			column: {
				name: "Name",
				kind: "Art",
			},
			kind: {
				project: "Projekt",
				dataspace: "Dataspace",
			},
			makePersonal: "{name} persönlich machen",
			personalConfirm: "{name} persönlich machen?",
			madePersonal: "{name} ist jetzt persönlich.",
			moved: "{n} Space in diese Abteilung verschoben. | {n} Spaces in diese Abteilung verschoben.",
			move: {
				action: "Spaces hierher verschieben",
				title: "Spaces in {department} verschieben",
				note: "Der Abteilungszugriff eines Spaces wird beim Verschieben zurückgesetzt: Die Eigentümer dieser Abteilung werden zu Admins, und niemand sonst erreicht ihn über die Abteilung, bis Sie weitere Rollen zuordnen. Von Hand hinzugefügte Personen behalten ihren Zugriff.",
				none: "Sie haben kein Projekt und keinen Dataspace, die Sie hierher verschieben dürfen. Dafür brauchen Sie das Recht, den Space zu löschen, und bei einem Space in einer anderen Abteilung das Recht, deren Spaces zu verwalten.",
				confirm: "{n} Space verschieben | {n} Spaces verschieben",
			},
			choose: {
				search: "Spaces suchen…",
				label: "Spaces",
			},
		},
		license: {
			title: "Lizenz",
			note: "Wird stündlich beim Lizenzserver geprüft",
			unnamed: "Gültige Lizenz",
			valid: "Lizenziert für {label}.",
			validNote: "Zuletzt bestätigt am {date}.",
			overdue:
				"Der Lizenzserver hat diese Lizenz in letzter Zeit nicht bestätigt. Die Lizenz läuft in {n} Tag ab, wenn keine Prüfung gelingt. | Der Lizenzserver hat diese Lizenz in letzter Zeit nicht bestätigt. Die Lizenz läuft in {n} Tagen ab, wenn keine Prüfung gelingt.",
			overdueNote: "Die Lizenz läuft am {date} ab.",
			expired: "Seit {date} keine bestätigte Lizenz.",
			expiredNote:
				"Der Lizenzserver hat die Lizenz seit 30 Tagen nicht bestätigt. Ersetzen Sie den Schlüssel, oder wenden Sie sich an LiveReader.",
			missing: "Für diese Abteilung ist keine Lizenz bestätigt.",
			missingNote:
				"Ersetzen Sie den Schlüssel durch einen, den LiveReader für diese Abteilung ausgestellt hat.",
			facts: {
				label: "Lizenziert für",
				status: "Status",
				answer: "Antwort des Lizenzservers",
				floatingSeats: "Floating-Plätze",
				namedSeats: "Namentliche Plätze",
				designer: "Designer",
				keyHint: "Schlüssel",
				lastCheck: "Zuletzt bestätigt",
				graceEnds: "Karenzzeit endet",
				graceLeft: "noch {n} Tag | noch {n} Tage",
			},
			status: {
				valid: "Gültig",
				overdue: "Überfällig",
				expired: "Abgelaufen",
				missing: "Fehlt",
			},
			answers: {
				granted: "Erteilt",
				disabled: "Deaktiviert",
				"bound-elsewhere": "An einen anderen Server gebunden",
				unknown: "Schlüssel unbekannt",
				invalid: "Kein gültiger Schlüssel",
				unconfirmed: "Keine Antwort",
			},
			on: "Mitglieder dürfen ihn nutzen",
			off: "Nicht enthalten",
			lastError: "Die letzte Prüfung ist fehlgeschlagen ({date})",
			failures: {
				department_license_disabled:
					"Laut Lizenzserver ist diese Lizenz deaktiviert. Die Karenzzeit läuft ab der letzten Bestätigung weiter.",
				department_license_bound_elsewhere:
					"Laut Lizenzserver ist dieser Schlüssel an einen anderen Server gebunden.",
				department_license_unknown: "Der Lizenzserver kennt diesen Schlüssel nicht.",
				department_license_invalid: "Laut Lizenzserver ist das kein gültiger Schlüssel.",
				department_license_unconfirmed:
					"Der Lizenzserver hat für diesen Schlüssel nicht geantwortet. Das ist kein Widerruf: Die Lizenz gilt weiter, solange die Karenzzeit läuft.",
			},
			rekey: {
				action: "Schlüssel ersetzen",
				label: "Neuer Lizenzschlüssel",
				help: "Wird vor dem Speichern beim Lizenzserver geprüft; lehnt er ihn ab, bleibt der bisherige Schlüssel. Die Abteilung behält ihre Mitglieder, Rollen und Spaces.",
				submit: "Prüfen und speichern",
				saved: "Der neue Lizenzschlüssel ist bestätigt und gespeichert.",
			},
			reveal: "Schlüssel anzeigen",
			hide: "Schlüssel verbergen",
		},
		remove: {
			title: "Abteilung löschen",
			note: "Beim Löschen werden auch die Mitglieder und Rollen der Abteilung entfernt. Eine Abteilung mit einem Projekt oder Dataspace lässt sich nicht löschen, ebenso wenig die Standardabteilung.",
			action: "Abteilung löschen",
			confirm: "{name} löschen?",
			confirmNote:
				"Die Mitglieder und Rollen der Abteilung werden ebenfalls gelöscht. Das lässt sich nicht rückgängig machen.",
			deleted: "{name} gelöscht.",
		},
		members: {
			title: "Mitglieder",
			invite: "Per E-Mail einladen",
			role: "Rolle",
			person: "Person",
			you: "Sie",
			empty: "Diese Abteilung hat noch keine eigenen Mitglieder.",
			roleChanged: "Rolle von {name} geändert.",
			remove: "{name} entfernen",
			removeConfirm: "{name} entfernen?",
			removeNote:
				"Die Person verliert sofort alles, was ihr die Rollen der Abteilung gewährt haben.",
			removeNoteDefault:
				"Alle sind Mitglied der Standardabteilung, daher bleibt die Person Mitglied — mit der Standardrolle statt dieser.",
			removed: "{name} entfernt.",
			leave: "{department} verlassen",
			leaveConfirm: "{name} verlassen?",
			leaveAction: "Verlassen",
			leaveNote:
				"Sie verlieren, was Ihnen Ihre Rolle hier gewährt hat. Ein Eigentümer kann Sie wieder einladen.",
			left: "Sie haben {name} verlassen.",
			implicit:
				"Sie sind Mitglied, weil alle Mitglied der Standardabteilung sind. Es gibt keine eigene Mitgliedschaft, die Sie verlassen oder ändern könnten.",
		},
		invitations: {
			dialogTitle: "In diese Abteilung einladen",
			note: "Die eingeladene Person erhält einen Link per E-Mail und kann, sobald ihr Kontodienst die Adresse bestätigt hat, auch im Bereich „Einladungen“ ihres Kontos antworten. Mit der gewählten Rolle tritt sie erst bei, wenn sie annimmt: Niemand wird einer Abteilung hinzugefügt, ohne gefragt worden zu sein.",
		},
		seats: {
			title: "Plätze",
			note: "Wer einen Prozess öffnet, belegt einen Platz — ob er darin arbeitet oder nur zusieht. Die Prozessliste und die Einstellungen belegen keinen.",
			refresh: "Aktualisieren",
			kind: {
				named: "Namentlicher Platz",
				floating: "Floating-Platz",
			},
			noneInLicense: "Keine in der Lizenz",
			floating: {
				label: "Floating-Plätze",
				reading: "{used} von {total} belegt",
			},
			named: {
				label: "Namentliche Plätze",
				reading: "{used} von {total} vergeben",
				title: "Namentliche Plätze",
				note: "Ein namentlicher Platz ist für ein Mitglied reserviert, das auch dann hineinkommt, wenn alle Floating-Plätze belegt sind. Die namentlichen Plätze der Lizenz gehen an die Mitglieder, denen zuerst einer zugeteilt wurde, in der Reihenfolge der Zuteilung. Einen Platz zuzuteilen oder zu entziehen, entfernt niemanden.",
				column: "Namentlicher Platz",
				switch: "Namentlicher Platz für {name}",
				holds: "Hat einen Platz",
				waiting:
					"Wartet: Die Lizenz hat keine namentlichen Plätze | Wartet: Die Lizenz hat einen namentlichen Platz | Wartet: Die Lizenz hat {n} namentliche Plätze",
				waitingBanner:
					"Einem Mitglied ist ein namentlicher Platz zugeteilt, den die Lizenz nicht abdeckt | {n} Mitgliedern ist ein namentlicher Platz zugeteilt, den die Lizenz nicht abdeckt",
				waitingNote:
					"Bis ein namentlicher Platz frei wird, nutzen sie einen Floating-Platz wie alle anderen. Nichts geht verloren: Die namentlichen Plätze der Lizenz gehen an die Mitglieder, denen zuerst einer zugeteilt wurde.",
				given: "{name} hat jetzt einen namentlichen Platz.",
				taken: "{name} hat jetzt keinen namentlichen Platz mehr.",
				empty: "Diese Abteilung hat keine eigenen Mitglieder, denen sie einen namentlichen Platz geben könnte.",
			},
			peak: "Höchste Zahl gleichzeitig belegter Floating-Plätze",
			refused: "Abgelehnte Anfragen nach einem Floating-Platz",
			since: "Gezählt seit {date}.",
			nothingCounted: "Es wurde noch nichts gezählt.",
			over: {
				title: "Es sind mehr Floating-Plätze belegt, als die Lizenz jetzt hat ({used} von {total})",
				note: "Niemand wird entfernt: Wer einen Platz hat, behält ihn. Niemand sonst bekommt einen, bis weniger als {total} belegt sind.",
			},
			active: {
				title: "Gerade belegt",
				note: "Wer gerade einen Platz hat. Das ändert sich, sobald jemand einen Prozess öffnet oder verlässt; mit „Aktualisieren“ lesen Sie den Stand neu.",
				column: "Platz",
				empty: "Gerade hat niemand einen Platz.",
			},
		},
		roles: {
			title: "Abteilungsrollen",
			locked: "Gesperrt",
			memberCount: "Keine Mitglieder | {n} Mitglied | {n} Mitglieder",
			sections: {
				department: "Abteilung",
				people: "Personen",
				spaces: "Spaces",
				license: "Lizenz",
			},
			ops: {
				read: "Die Abteilung sehen",
				update: "Name und Beschreibung bearbeiten",
				delete: "Die Abteilung löschen",
				members: "Mitglieder verwalten",
				invitations: "Einladungen verwalten",
				roles: "Rollen verwalten",
				seats: "Plätze verwalten",
				spaces: "Spaces verwalten",
				moveIn: "Spaces in die Abteilung verschieben",
				projects: "Projekte erstellen",
				dataspaces: "Dataspaces erstellen",
				license: "Den Lizenzschlüssel ersetzen",
			},
		},
		errors: {
			invalid_name: "Der Name muss 1 bis 100 Zeichen lang sein.",
			invalid_description: "Die Beschreibung darf höchstens 2000 Zeichen lang sein.",
			department_name_taken: "Es gibt schon eine Abteilung mit diesem Namen.",
			department_key_unknown:
				"Der Lizenzserver kennt diesen Schlüssel nicht. Prüfen Sie ihn und versuchen Sie es erneut.",
			department_key_invalid: "Laut Lizenzserver ist das kein gültiger Abteilungsschlüssel.",
			invalid_license_key: "Ein Lizenzschlüssel ist ein einzelnes Wort, ohne Leerzeichen.",
			department_key_disabled:
				"Laut Lizenzserver ist die Lizenz dieser Abteilung deaktiviert. Wenden Sie sich an LiveReader.",
			department_key_bound_elsewhere:
				"Dieser Schlüssel ist an einen anderen Server gebunden. Wenden Sie sich an LiveReader, um ihn zu verschieben.",
			department_key_in_use:
				"Eine andere Abteilung auf diesem Server verwendet diesen Schlüssel schon.",
			department_key_unconfirmed:
				"Der Lizenzserver hat für diesen Schlüssel nicht geantwortet, darum wurde nichts gespeichert. Versuchen Sie es später erneut.",
			license_required:
				"Dieser Server hat keine gültige Lizenz, darum lässt sich keine Abteilung aktivieren und kein Abteilungsschlüssel ersetzen. Eine Serveradministratorin oder ein Serveradministrator kann im Kontrollzentrum einen Lizenzschlüssel eingeben.",
			license_disabled:
				"Laut Lizenzserver ist die Lizenz dieses Servers deaktiviert. Wenden Sie sich an LiveReader.",
			license_server_unreachable:
				"Der Lizenzserver war nicht erreichbar. Versuchen Sie es später erneut.",
			license_response_invalid: "Die Antwort des Lizenzservers ließ sich nicht lesen.",
			department_not_empty:
				"In dieser Abteilung liegt noch ein Projekt oder ein Dataspace. Verschieben oder löschen Sie es zuerst.",
			department_is_default: "Die Standardabteilung lässt sich nicht löschen.",
			last_owner:
				"Eine Abteilung braucht mindestens einen Eigentümer. Machen Sie zuerst jemand anderen zum Eigentümer.",
			default_department:
				"Alle sind Mitglied der Standardabteilung, darum lässt sie sich nicht verlassen.",
			already_member: "Diese Person ist schon Mitglied der Abteilung.",
			already_invited:
				"Für diese E-Mail-Adresse ist bereits eine Einladung offen. Ziehen Sie sie zurück oder senden Sie sie erneut.",
			self_invite: "Sie können sich nicht selbst einladen.",
			invitation_mail_disabled:
				"Der Einladungsversand per E-Mail ist ausgeschaltet oder dieser Server kann keine E-Mails senden. Eine Einladung kann daher nicht erneut gesendet werden. Ziehen Sie sie zurück und laden Sie erneut ein, um einen Link zum Weitergeben zu erhalten.",
			invalid_role: "Das ist keine Rolle dieser Abteilung.",
			invalid_entitlement: "Eine Abteilungsrolle enthält nur Abteilungsberechtigungen.",
			role_exists: "In der Abteilung gibt es schon eine Rolle mit diesem Namen.",
			role_in_use:
				"Diese Rolle ist noch Mitgliedern zugewiesen. Weisen Sie ihnen zuerst eine andere zu.",
			role_locked:
				"Was die Eigentümerrolle gewährt, lässt sich nicht ändern. Duplizieren Sie die Rolle, um eine eigene zu erhalten, die Sie bearbeiten können.",
			role_built_in: "Eine eingebaute Rolle lässt sich weder umbenennen noch löschen.",
			not_a_member:
				"Nur ein eigenes Mitglied kann einen namentlichen Platz bekommen. Diese Person ist Mitglied, weil alle es sind.",
			missing_entitlement: "Ihre Systemrolle erlaubt das nicht.",
		},
	},
};
